import { Helmet } from "react-helmet-async";

const SITE = "UID Infotech";
const BASE_URL = "https://uidinfotech.online";
const ORG_LEGAL_NAME = "UID Infotech";
const PHONE = "+91-8285577472";
const EMAIL = "support@uidinfotech.online";
const ADDRESS = {
  streetAddress: "Sector 62",
  addressLocality: "Noida",
  addressRegion: "Uttar Pradesh",
  postalCode: "201301",
  addressCountry: "IN",
};

/** Build the Organization + LocalBusiness JSON-LD schema once. */
function organizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    name: ORG_LEGAL_NAME,
    legalName: ORG_LEGAL_NAME,
    url: BASE_URL,
    logo: `${BASE_URL}/logo.png`,
    image: `${BASE_URL}/og-default.png`,
    description: "Enterprise IT services, server management, cloud, web & Android development, SEO and instant expert support for India.",
    telephone: PHONE,
    email: EMAIL,
    address: {
      "@type": "PostalAddress",
      ...ADDRESS,
    },
    areaServed: { "@type": "Country", name: "India" },
    priceRange: "₹₹",
    serviceType: [
      "Cloud Hosting",
      "Server Management",
      "Web Development",
      "Android App Development",
      "SEO",
      "Hardware Repair",
      "PVC ID Card Printing",
    ],
    sameAs: [
      "https://uidinfotech.online",
    ],
    openingHoursSpecification: {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
      opens: "09:30",
      closes: "19:00",
    },
  };
}

function breadcrumbSchema(crumbs) {
  if (!Array.isArray(crumbs) || crumbs.length === 0) return null;
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: `${BASE_URL}${c.path}`,
    })),
  };
}

export default function Seo({
  title,
  description,
  path = "",
  image,
  type = "website",
  noindex = false,
  breadcrumbs = null, // [{name, path}]
  extraSchema = null, // optional additional JSON-LD object
}) {
  const fullTitle = title ? `${title} · ${SITE}` : `${SITE} — Enterprise IT & Cloud Solutions`;
  const desc = description || "Enterprise IT services, server management, cloud, web/Android development, SEO and instant expert support. 24×7 NOC. SLA-backed.";
  const url = `${BASE_URL}${path}`;
  const ogImage = image || `${BASE_URL}/og-default.png`;

  const orgJson = organizationSchema();
  const breadcrumbJson = breadcrumbSchema(breadcrumbs);

  return (
    <Helmet>
      <html lang="en-IN" />
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      <link rel="canonical" href={url} />
      <link rel="alternate" href={url} hrefLang="en-IN" />
      <link rel="alternate" href={url} hrefLang="x-default" />

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content="en_IN" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD: Organization / LocalBusiness */}
      <script type="application/ld+json">{JSON.stringify(orgJson)}</script>

      {/* JSON-LD: Breadcrumbs (optional per page) */}
      {breadcrumbJson && (
        <script type="application/ld+json">{JSON.stringify(breadcrumbJson)}</script>
      )}

      {/* JSON-LD: Extra (e.g. Service, FAQPage — set per page) */}
      {extraSchema && (
        <script type="application/ld+json">{JSON.stringify(extraSchema)}</script>
      )}
    </Helmet>
  );
}
